import { EmptyState } from '@bp/ui-components';
import {
  type DragLayerMonitorProps,
  type DropOptions,
  getBackendOptions,
  getDescendants,
  getParents,
  MultiBackend,
  type NodeModel,
  Tree,
} from '@minoru/react-dnd-treeview';
import { graphql } from 'client/gql';
import { type FC, type Ref, useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import styles from './TeachingUnitsTree.module.scss';
import { TeachingUnitsTreeDragPreview } from './TeachingUnitsTreeDragPreview';
import { TeachingUnitsTreeDropPreview } from './TeachingUnitsTreeDropPreview';
import { TeachingUnitsTreeItem } from './TeachingUnitsTreeItem';
import { type CustomData, TreeItemType } from './types';

const DeleteTeachingTopicMutationDocument = graphql(`
  mutation BpDeleteTeachingTopic($where: TeachingTopicWhere!) {
    deleteTeachingTopics(where: $where) {
      nodesDeleted
    }
  }
`);
const DeleteTeachingUnitsMutationDocument = graphql(`
  mutation BpDeleteTeachingUnits($where: TeachingUnitWhere!) {
    deleteTeachingUnits(where: $where) {
      nodesDeleted
    }
  }
`);
const UpdateTeachingTopicsConnectionsMutationDocument = graphql(`
  mutation BpUpdateTeachingTopicsConnections($where: TeachingTopicWhere!, $update: TeachingTopicUpdateInput!) {
    updateTeachingTopics(update: $update, where: $where) {
      info {
        relationshipsCreated
        relationshipsDeleted
      }
      teachingTopics {
        uuid
        __typename
        topicParentConnection {
          __typename
          edges {
            __typename
            node {
              uuid
              __typename
            }
          }
        }
      }
    }
  }
`);
const UpdateTeachingUnitMutationDocument = graphql(`
  mutation BpUpdateTeachingUnitInTeachingUnitsTree($where: TeachingUnitWhere!, $update: TeachingUnitUpdateInput!) {
    updateTeachingUnits(where: $where, update: $update) {
      info {
        nodesCreated
        nodesDeleted
        relationshipsCreated
        relationshipsDeleted
      }
      teachingUnits {
        uuid
        __typename
        title
        description
        topicConnection {
          __typename
          edges {
            __typename
            node {
              uuid
              __typename
            }
          }
        }
      }
    }
  }
`);

const TeachingUnitTreeQueryDocument = graphql(`
  query TeachingUnitTreeData($courseUuid: ID!) {
    teachingUnits(where: { group: { uuid: $courseUuid } }) {
      uuid
      __typename
      title
      topic {
        uuid
        __typename
      }
    }
    teachingTopics(where: { group: { uuid: $courseUuid } }) {
      uuid
      __typename
      title
      topicParentConnection {
        __typename
        edges {
          __typename
          node {
            uuid
            __typename
          }
        }
      }
    }
    groups(where: { uuid: $courseUuid }) {
      uuid
      __typename
    }
  }
`);

type TeachingUnitsTreeProps = {
  courseUuid: string | undefined;
  activeTeachingUnitUuid?: string | null;
  isEditable?: boolean;
  onTeachingUnitSelect: (uuid: string) => void;
  onTeachingUnitAdd?: (parentUuid: string | null) => void;
  onTopicAdd?: (parentUuid: string) => void;
  onTopicEdit?: (topicUuid: string) => void;
};

export const TeachingUnitsTree: FC<TeachingUnitsTreeProps> = ({
  courseUuid,
  isEditable = false,
  activeTeachingUnitUuid,
  onTeachingUnitSelect,
  onTeachingUnitAdd,
  onTopicAdd,
  onTopicEdit,
}) => {
  const { t } = useTranslation();

  const [tree, setTree] = useState<NodeModel<CustomData>[] | null>(null);

  const [{ data }] = useQuery({
    query: TeachingUnitTreeQueryDocument,
    variables: {
      courseUuid: courseUuid!,
    },
    pause: courseUuid === undefined,
  });

  const teachingUnits = data?.teachingUnits;
  const teachingTopics = data?.teachingTopics;

  const rootTopic = teachingTopics?.find((teachingTopic) => {
    return teachingTopic.topicParentConnection.edges.length === 0;
  });

  const [, connectTopic] = useMutation(UpdateTeachingTopicsConnectionsMutationDocument);
  const [, updateTeachingUnit] = useMutation(UpdateTeachingUnitMutationDocument);
  const [, deleteTeachingUnit] = useMutation(DeleteTeachingUnitsMutationDocument);
  const [, deleteTopic] = useMutation(DeleteTeachingTopicMutationDocument);

  useEffect(() => {
    const _tree: NodeModel<CustomData>[] =
      teachingTopics
        ?.map((topic) => {
          const units = teachingUnits?.filter((unit) => {
            return unit.topic.uuid === topic.uuid;
          });
          // do  we have teachingunits as children?
          if (units && units.length > 0) {
            return [
              {
                id: topic.uuid,
                parent: topic.topicParentConnection.edges[0]?.node.uuid ?? 0,
                droppable: true,
                text: topic.title,
                data: {
                  type: TreeItemType.topic,
                },
              },
              ...units.map((tu) => {
                return {
                  id: tu.uuid,
                  parent: topic.uuid ?? 0,
                  droppable: false,
                  text: tu.title,
                  data: {
                    type: TreeItemType.teachingUnit,
                  },
                };
              }),
            ];
          }
          // no teachingUnit
          return {
            id: topic.uuid,
            parent: topic.topicParentConnection.edges[0]?.node.uuid ?? 0,
            droppable: true,
            text: topic.title,
            data: {
              type: TreeItemType.topic,
            },
          };
        })
        // flatten the array, we receive an array of arrays if we have teachingUnits as children
        .flat(2) ?? [];
    setTree(_tree);
  }, [teachingTopics, teachingUnits]);

  const handleTopicMove = async (newTree: NodeModel<CustomData>[], options: DropOptions<CustomData>) => {
    setTree(newTree);
    const topicUuid = options.dragSourceId;
    const targetUuid = options.dropTargetId;

    await connectTopic({
      update: {
        topicParent: {
          disconnect: {},
          connect: {
            where: {
              node: {
                uuid: targetUuid as string,
              },
            },
          },
        },
      },
      where: {
        uuid: topicUuid as string,
      },
    });
  };

  const handleTeachingUnitMove = async (newTree: NodeModel<CustomData>[], options: DropOptions<CustomData>) => {
    setTree(newTree);

    const topicUuid = options.dragSourceId;
    const targetUuid = options.dropTargetId;

    updateTeachingUnit({
      where: {
        uuid: topicUuid as string,
      },
      update: {
        topic: {
          connect: {
            where: {
              node: {
                uuid: targetUuid as string,
              },
            },
          },
          disconnect: {},
        },
      },
    });
  };

  const handleDeleteTeachingUnit = async (uuid: string) => {
    if (tree) {
      const deleteIds = [uuid, ...getDescendants(tree, uuid).map((node) => node.id)];
      const newTree = tree.filter((node) => !deleteIds.includes(node.id));
      setTree(newTree);
    }

    await deleteTeachingUnit({
      where: {
        uuid: uuid,
      },
    });
  };

  const handleDeleteTopic = async (uuid: string) => {
    if (tree) {
      const deleteIds = [uuid, ...getDescendants(tree, uuid).map((node) => node.id)];
      const newTree = tree.filter((node) => !deleteIds.includes(node.id));
      setTree(newTree);
    }
    await deleteTopic(
      {
        where: {
          uuid: uuid,
        },
      },
      { additionalTypenames: ['TeachingTopic'] },
    );
  };

  return (
    <div className={styles['teaching-units-tree']}>
      {teachingTopics?.length === 0 ? (
        <EmptyState hideIcon subtitle={t('teachingUnits.emptyMessage')} padding='m' />
      ) : (
        <DndProvider backend={MultiBackend} options={getBackendOptions()}>
          {tree && (
            <Tree<CustomData>
              sort={true}
              canDrag={() => isEditable}
              tree={tree}
              rootId={rootTopic?.uuid || 0}
              render={(node, { isOpen, onToggle, handleRef }) => {
                // convert handleRef to a function that returns a ref or undefined
                const dragHandleRef: Ref<HTMLDivElement> | undefined = () => {
                  if (handleRef.current === null) {
                    return undefined;
                  }
                  return handleRef as Ref<HTMLDivElement>;
                };
                return (
                  <TeachingUnitsTreeItem
                    node={node}
                    dragHandleRef={dragHandleRef}
                    childrenCount={tree ? tree.filter((item) => item.parent === node.id).length : 0}
                    isEditable={isEditable}
                    isOpen={isOpen}
                    isSelected={node.id === activeTeachingUnitUuid}
                    onToggle={onToggle}
                    onSelect={(node) => {
                      onTeachingUnitSelect(node.id as string);
                    }}
                    onTopicEdit={onTopicEdit ? (topicUuid) => onTopicEdit(topicUuid) : undefined}
                    onTopicAdd={onTopicAdd ? (parentUuid) => onTopicAdd(parentUuid) : undefined}
                    onTeachingUnitAdd={onTeachingUnitAdd ? (parentUuid) => onTeachingUnitAdd(parentUuid) : undefined}
                    onDelete={node.droppable ? handleDeleteTopic : handleDeleteTeachingUnit}
                  />
                );
              }}
              dragPreviewRender={(monitorProps: DragLayerMonitorProps<CustomData>) => (
                <TeachingUnitsTreeDragPreview text={monitorProps.item.text} type={monitorProps.item.data?.type} />
              )}
              placeholderRender={(node: NodeModel<CustomData>) => (
                <TeachingUnitsTreeDropPreview text={node.text} type={node.data?.type} />
              )}
              onDrop={(tree, options) => {
                if (options?.dragSource?.data?.type === 'topic') {
                  handleTopicMove(tree, options);
                }
                if (options?.dragSource?.data?.type === 'teachingUnit') {
                  handleTeachingUnitMove(tree, options);
                }
                (document.activeElement as HTMLDivElement).blur();
              }}
              enableAnimateExpand
              classes={{
                draggingSource: styles['dragging-source'],
                dropTarget: styles['drop-target'],
              }}
              initialOpen={
                activeTeachingUnitUuid ? getParents(tree, activeTeachingUnitUuid).map((parent) => parent.id) : false
              }
            />
          )}
        </DndProvider>
      )}
      {isEditable && <div className={styles['drag-hint']}>{t('teachingUnits.dragHint')}</div>}
    </div>
  );
};
